import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { addDays, addMinutes, format, isSameDay, isValid, parse, startOfDay } from 'date-fns';
import { api } from '../api';
import { uc } from '../unifiedContactsApi';
import DialogShell from '../kit/DialogShell';
import { fromApi } from '../kit/time';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { confirmDialog } from '@/components/dialogs/DialogHost';

// Book / reschedule dialog (ticket 143 P4). Create: contact, calendar,
// optional assignee (owner/admin), date + time + length, optional title.
// `fixedContact` (the contact profile's Appointments tab) pre-picks the contact
// and removes "Change".
// Reschedule: date + time + length only, on the appointment's own calendar.
// Length defaults to the calendar's slot (create) or the current length
// (reschedule) and goes to the API as `end_at`.
//
// Free slots are offered first. A time outside them asks "Book it anyway?";
// yes sends `override: true` and GHL skips its availability check. Create is
// GHL-first server-side, so on any error the dialog stays open with the API's
// message and nothing has changed. Reschedule is optimistic (ticket 189): once
// the availability prompt is answered the dialog hands the new time to the
// page (`onReschedule`) and closes; the page moves the block at once, calls
// GHL, and puts it back with a toast if GHL refuses.

const OTHER = '__other';
const AUTO = '__auto';
const DURATIONS = [15, 30, 45, 60, 90, 120];
const DEFAULT_MINUTES = 30;

// ── pure helpers (exported for tests) ────────────────────────────────────

// "YYYY-MM-DD" → the viewer's local day as [00:00, next 00:00) in ISO UTC.
// addDays steps calendar days, so a 23/25-hour DST day is still one day.
export function dayWindow(dateStr) {
  const d = /^\d{4}-\d{2}-\d{2}$/.test(dateStr || '') ? parse(dateStr, 'yyyy-MM-dd', new Date()) : null;
  if (!d || !isValid(d)) return null;
  const from = startOfDay(d);
  return { from: from.toISOString(), to: addDays(from, 1).toISOString() };
}

// Is `date` exactly one of the offered slots (ISO strings from the API)?
export function isFreeSlot(slots, date) {
  if (!date || !isValid(date)) return false;
  return (slots || []).some((s) => fromApi(s).getTime() === date.getTime());
}

// Only the slots whose LOCAL date is `dateStr` (GHL can hand back a neighbouring day).
export function slotsOnDay(slots, dateStr) {
  const d = /^\d{4}-\d{2}-\d{2}$/.test(dateStr || '') ? parse(dateStr, 'yyyy-MM-dd', new Date()) : null;
  if (!d || !isValid(d)) return [];
  return (slots || []).filter((s) => {
    const t = fromApi(s);
    return isValid(t) && isSameDay(t, d);
  });
}

// Whole minutes from start to end (API datetimes); null when either is
// missing or the end isn't after the start.
export function minutesBetween(startAt, endAt) {
  if (!startAt || !endAt) return null;
  const a = fromApi(startAt);
  const b = fromApi(endAt);
  if (!isValid(a) || !isValid(b)) return null;
  const min = Math.round((b.getTime() - a.getTime()) / 60000);
  return min > 0 ? min : null;
}

// 30 → "30 min", 60 → "1 hr", 90 → "1 hr 30 min".
export function durationLabel(min) {
  const h = Math.floor(min / 60);
  const m = min % 60;
  return [h ? `${h} hr` : '', m ? `${m} min` : ''].filter(Boolean).join(' ');
}

// The standard lengths plus `extra` (the calendar's slot, the current length), sorted.
export function durationOptions(extra) {
  const all = new Set(DURATIONS);
  if (Number.isFinite(extra) && extra > 0) all.add(extra);
  return [...all].sort((a, b) => a - b);
}

// That day's free slots (ISO UTC strings) on a calendar.
export async function fetchFreeSlots(calendarId, dateStr, userId) {
  const win = dayWindow(dateStr);
  if (!calendarId || !win) return [];
  const params = { from: win.from, to: win.to };
  if (userId) params.user_id = userId;
  const { data } = await api.get(`/v2/calendars/${encodeURIComponent(calendarId)}/free-slots`, { params });
  return slotsOnDay(Array.isArray(data?.slots) ? data.slots : [], dateStr);
}

// The "outside availability" prompt. true → send override.
export const confirmOutsideAvailability = (calendarName) => confirmDialog({
  title: 'Outside availability',
  description: `This time isn't open on ${calendarName || 'this calendar'}. Book it anyway?`,
  confirmText: 'Book anyway',
});

const localDate = (d) => format(d, 'yyyy-MM-dd');
const localTime = (d) => format(d, 'HH:mm');
const slotTime = (s) => localTime(fromApi(s));

function startOf(date, time) {
  if (!date || !time) return null;
  const d = parse(`${date} ${time}`, 'yyyy-MM-dd HH:mm', new Date());
  return isValid(d) ? d : null;
}

// The API's `detail`: a string, or `{message, code}` on create / reschedule
// (code 'ghl_rejected' = GoHighLevel refused the time).
export function errorDetail(e) {
  const d = e?.response?.data?.detail;
  if (typeof d === 'string') return { message: d, code: null };
  return { message: typeof d?.message === 'string' ? d.message : null, code: d?.code || null };
}

export const detailOf = (e, fallback) => errorDetail(e).message || fallback;

// GHL refused a time we sent without override. true → resend with override.
export const confirmGhlRefused = (message, confirmText) => confirmDialog({
  title: 'GoHighLevel refused this time',
  description: `GoHighLevel refused this time: ${message}. Book it anyway?`,
  confirmText,
});

// ── contact picker ───────────────────────────────────────────────────────

function ContactPicker({ value, onChange, locked = false }) {
  const [q, setQ] = useState('');
  const [hits, setHits] = useState([]);
  const [busy, setBusy] = useState(false);
  const seq = useRef(0);

  useEffect(() => {
    const term = q.trim();
    if (!term) { setHits([]); setBusy(false); return undefined; }
    const mine = ++seq.current;
    setBusy(true);
    const t = setTimeout(() => {
      uc.search(term, 8)
        .then((r) => { if (mine === seq.current) setHits(Array.isArray(r?.contacts) ? r.contacts : []); })
        .catch(() => { if (mine === seq.current) setHits([]); })
        .finally(() => { if (mine === seq.current) setBusy(false); });
    }, 250);
    return () => clearTimeout(t);
  }, [q]);

  if (value) {
    return (
      <div className="flex items-center justify-between gap-2 rounded-md border border-slate-200 px-3 py-2 text-sm" data-testid="book-contact-selected">
        <span className="truncate font-medium text-brand-navy">{value.display_name || 'Contact'}</span>
        {!locked && (
          <button type="button" className="text-xs text-indigo-700 underline shrink-0" onClick={() => onChange(null)} data-testid="book-contact-clear">
            Change
          </button>
        )}
      </div>
    );
  }
  return (
    <div>
      <Input
        id="book-contact"
        value={q}
        onChange={(e) => setQ(e.target.value)}
        placeholder="Search name, email or phone"
        autoComplete="off"
        data-testid="book-contact-search"
      />
      {q.trim() && (
        <ul className="mt-1 max-h-40 overflow-y-auto rounded-md border border-slate-200 bg-white text-sm" data-testid="book-contact-results">
          {busy && <li className="px-3 py-2 text-xs text-slate-400">Searching…</li>}
          {!busy && hits.length === 0 && <li className="px-3 py-2 text-xs text-slate-400">No matching contacts.</li>}
          {hits.map((c) => (
            <li key={c.contact_id}>
              <button
                type="button"
                className="w-full px-3 py-2 text-left hover:bg-slate-50"
                onClick={() => onChange(c)}
                data-testid={`book-contact-option-${c.contact_id}`}
              >
                <span className="block truncate font-medium text-brand-navy">{c.display_name || 'Unnamed'}</span>
                {c.emails?.[0]?.value && <span className="block truncate text-xs text-slate-500">{c.emails[0].value}</span>}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// ── form ─────────────────────────────────────────────────────────────────

function Field({ label, htmlFor, children }) {
  return (
    <div className="space-y-1">
      <Label htmlFor={htmlFor} className="text-xs text-slate-600">{label}</Label>
      {children}
    </div>
  );
}

function BookForm({ mode, appointment, initialStart, fixedContact, canViewAll, me, onClose, onSaved, onReschedule }) {
  const reschedule = mode === 'reschedule';
  const first = useMemo(() => {
    if (reschedule) {
      const d = fromApi(appointment?.start_at);
      return isValid(d) ? d : null;
    }
    return initialStart && isValid(initialStart) ? initialStart : null;
  }, [reschedule, appointment, initialStart]);

  const [contact, setContact] = useState(fixedContact || null);
  const [calendars, setCalendars] = useState(null); // null = loading
  const [calendarId, setCalendarId] = useState(reschedule ? appointment?.calendar_id || '' : '');
  const [assignee, setAssignee] = useState(null); // null = untouched → default
  const [date, setDate] = useState(localDate(first || new Date()));
  const [time, setTime] = useState(first ? localTime(first) : '');
  const [other, setOther] = useState(false);
  const [duration, setDuration] = useState(null); // null = untouched → default
  const [title, setTitle] = useState('');
  const [slots, setSlots] = useState({ loading: false, list: [], failed: false });
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  // Calendars (create only). One bookable calendar is picked for you.
  useEffect(() => {
    if (reschedule) return undefined;
    let alive = true;
    api.get('/v2/calendars')
      .then(({ data }) => {
        if (!alive) return;
        const list = Array.isArray(data) ? data : [];
        setCalendars(list);
        if (list.length === 1) setCalendarId(list[0].calendar_id);
      })
      .catch((e) => { if (alive) { setCalendars([]); setError(detailOf(e, 'Could not load calendars.')); } });
    return () => { alive = false; };
  }, [reschedule]);

  const calendar = reschedule ? null : (calendars || []).find((c) => c.calendar_id === calendarId);
  const calendarName = reschedule ? appointment?.calendar_name : calendar?.name;
  const team = useMemo(() => (calendar?.team || []).filter((t) => t.user_id), [calendar]);
  const showAssignee = !reschedule && canViewAll && team.length > 0;
  const assigneeId = showAssignee
    ? (assignee ?? (team.some((t) => t.user_id === me) ? me : AUTO))
    : AUTO;
  const slotUser = assigneeId === AUTO ? undefined : assigneeId;
  const defaultMinutes = (reschedule
    ? minutesBetween(appointment?.start_at, appointment?.end_at)
    : calendar?.slot_minutes) || DEFAULT_MINUTES;
  const minutes = duration ?? defaultMinutes;

  // That day's free slots for the calendar (+ assignee). A failure isn't fatal:
  // the server (GHL) still validates, we just can't pre-check.
  useEffect(() => {
    if (!calendarId || !dayWindow(date)) { setSlots({ loading: false, list: [], failed: false }); return undefined; }
    let alive = true;
    setSlots({ loading: true, list: [], failed: false });
    fetchFreeSlots(calendarId, date, reschedule ? appointment?.owner_id : slotUser)
      .then((list) => { if (alive) setSlots({ loading: false, list, failed: false }); })
      .catch(() => { if (alive) setSlots({ loading: false, list: [], failed: true }); });
    return () => { alive = false; };
  }, [calendarId, date, slotUser, reschedule, appointment?.owner_id]);

  const options = useMemo(() => {
    const seen = new Set();
    return slots.list
      .map((s) => ({ value: slotTime(s), label: format(fromApi(s), 'h:mm a') }))
      .filter((o) => (seen.has(o.value) ? false : seen.add(o.value)));
  }, [slots.list]);

  // A time carried in (clicked slot, current start) that isn't offered falls to "Other time…".
  useEffect(() => {
    if (slots.loading || !time || other) return;
    if (!options.some((o) => o.value === time)) setOther(true);
  }, [slots.loading, options, time, other]);

  const start = startOf(date, time);
  const end = start ? addMinutes(start, minutes) : null;
  const canSubmit = !saving && !slots.loading && !!start && (reschedule || (!!contact && !!calendarId));

  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setError('');
    setSaving(true);
    try {
      let override = false;
      if (calendarId && !slots.failed && !isFreeSlot(slots.list, start)) {
        if (!(await confirmOutsideAvailability(calendarName))) return;
        override = true;
      }
      if (reschedule) {
        onReschedule(appointment, { start_at: start.toISOString(), end_at: end.toISOString() }, override);
        onClose();
        return;
      }
      const send = (ov) => {
        const body = {
          contact_id: contact.contact_id, calendar_id: calendarId,
          start_at: start.toISOString(), end_at: end.toISOString(), override: ov,
        };
        if (title.trim()) body.title = title.trim();
        if (showAssignee && assigneeId !== AUTO) body.assignee_user_id = assigneeId;
        return api.post('/v2/appointments', body);
      };
      let res;
      try {
        res = await send(override);
      } catch (err) {
        const { message, code } = errorDetail(err);
        // GHL refused a time our free-slot list offered: let them override.
        if (override || code !== 'ghl_rejected') throw err;
        if (!(await confirmGhlRefused(message, 'Book anyway'))) { setError(message); return; }
        res = await send(true);
      }
      onSaved?.(res.data);
      onClose();
    } catch (err) {
      setError(detailOf(err, reschedule ? 'Could not reschedule.' : 'Could not book the appointment.'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-3" data-testid="book-form">
      {reschedule ? (
        <div className="rounded-md bg-slate-50 px-3 py-2 text-sm">
          <p className="truncate font-medium text-brand-navy">{appointment?.title || 'Appointment'}</p>
          <p className="truncate text-xs text-slate-500">
            {[appointment?.contact_name, appointment?.calendar_name].filter(Boolean).join(' · ') || '—'}
          </p>
        </div>
      ) : (
        <>
          <Field label="Contact" htmlFor="book-contact">
            <ContactPicker value={contact} onChange={setContact} locked={!!fixedContact} />
          </Field>
          <Field label="Calendar" htmlFor="book-calendar">
            <Select value={calendarId} onValueChange={(v) => { if (v) { setCalendarId(v); setAssignee(null); setDuration(null); } }} disabled={calendars === null}>
              <SelectTrigger id="book-calendar" data-testid="book-calendar">
                {calendars === null ? <Loader2 className="animate-spin" size={14} /> : <SelectValue placeholder={calendars.length ? 'Choose a calendar' : 'No calendars available'} />}
              </SelectTrigger>
              <SelectContent>
                {(calendars || []).map((c) => (
                  <SelectItem key={c.calendar_id} value={c.calendar_id}>{c.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          {showAssignee && (
            <Field label="Assigned to" htmlFor="book-assignee">
              <Select value={assigneeId} onValueChange={(v) => { if (v) setAssignee(v); }}>
                <SelectTrigger id="book-assignee" data-testid="book-assignee"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value={AUTO}>Automatic</SelectItem>
                  {team.map((t) => (
                    <SelectItem key={t.user_id} value={t.user_id}>{t.name || t.user_id}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
          )}
        </>
      )}

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <Field label="Date" htmlFor="book-date">
          <Input id="book-date" type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="book-date" />
        </Field>
        <Field label="Time" htmlFor="book-time">
          <Select
            value={other ? OTHER : time}
            disabled={!calendarId}
            onValueChange={(v) => {
              if (!v) return; // Radix reports '' while the option list is reloading
              if (v === OTHER) setOther(true);
              else { setOther(false); setTime(v); }
            }}
          >
            <SelectTrigger id="book-time" data-testid="book-time">
              {slots.loading ? <Loader2 className="animate-spin" size={14} /> : <SelectValue placeholder="Choose a time" />}
            </SelectTrigger>
            <SelectContent className="max-h-64">
              {options.map((o) => (
                <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
              ))}
              <SelectItem value={OTHER}>Other time…</SelectItem>
            </SelectContent>
          </Select>
        </Field>
      </div>
      {calendarId && !slots.loading && !slots.failed && options.length === 0 && (
        <p className="text-xs text-slate-500" data-testid="book-no-slots">No open times this day. Pick “Other time…” to book outside availability.</p>
      )}
      {other && (
        <Input
          type="time"
          aria-label="Other time"
          value={time}
          onChange={(e) => setTime(e.target.value)}
          data-testid="book-time-other"
        />
      )}

      <Field label="Duration" htmlFor="book-duration">
        <Select value={String(minutes)} onValueChange={(v) => { if (v) setDuration(Number(v)); }}>
          <SelectTrigger id="book-duration" data-testid="book-duration"><SelectValue /></SelectTrigger>
          <SelectContent>
            {durationOptions(minutes).map((m) => (
              <SelectItem key={m} value={String(m)}>{durationLabel(m)}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        {end && (
          <p className="text-xs text-slate-500" data-testid="book-ends">
            Ends {format(end, isSameDay(start, end) ? 'h:mm a' : 'MMM d, h:mm a')}
          </p>
        )}
      </Field>

      {!reschedule && (
        <Field label="Title (optional)" htmlFor="book-title">
          <Input id="book-title" value={title} maxLength={120} onChange={(e) => setTitle(e.target.value)} placeholder="Calendar default" data-testid="book-title" />
        </Field>
      )}

      {error && <p role="alert" className="text-sm text-red-600" data-testid="book-error">{error}</p>}

      <div className="flex flex-col-reverse gap-2 pt-1 sm:flex-row sm:justify-end">
        <Button type="button" variant="outline" onClick={onClose} disabled={saving} data-testid="book-cancel">Cancel</Button>
        <Button type="submit" disabled={!canSubmit} data-testid="book-submit">
          {saving && <Loader2 className="animate-spin" size={14} />}
          {reschedule ? 'Reschedule' : 'Book appointment'}
        </Button>
      </div>
    </form>
  );
}

export default function BookDialog({ open, mode = 'create', appointment, initialStart, fixedContact, canViewAll, me, onClose, onSaved, onReschedule }) {
  return (
    <DialogShell
      open={open}
      onOpenChange={(o) => { if (!o) onClose(); }}
      title={mode === 'reschedule' ? 'Reschedule appointment' : 'New appointment'}
      description={mode === 'reschedule'
        ? 'Shows here now, then saves to GoHighLevel. If it refuses, the time goes back.'
        : 'Saved to GoHighLevel first, then here.'}
      className="max-h-[90dvh] w-[calc(100vw-2rem)] overflow-y-auto sm:w-full"
    >
      <BookForm
        mode={mode}
        appointment={appointment}
        initialStart={initialStart}
        fixedContact={fixedContact}
        canViewAll={canViewAll}
        me={me}
        onClose={onClose}
        onSaved={onSaved}
        onReschedule={onReschedule}
      />
    </DialogShell>
  );
}
